import React from "react";
import { Avatar, Badge, Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SearchBar from "./SearchBar";
import StatusRibon from "./StatusRibon";
import MainAddictionRibon from "./MainAddictionRibon";
import DropDownComponent from "./DropDownComponent";
import DropDownComponentSorted from "./DropDownComponentSorted";
import CheckRibon from "./CheckRibon";
import "./ClientProfileHeaderModern.css";

function ClientProfileHeaderModern({
  clientData,
  formatDate,
  handleBackClick,
  handleOpenModalPhoto,
  handleProfilePictureChange,
  handleOpenStatusModal,
  handleOpenAddictionModal,
  isAllPreadmissionFieldsFilled,
  openStatusModal,
  openPrimaryAddictionModal,
  newClientStatus,
  newClientAddiction,
  handleClientStatusChange,
  handleClientAddictionChange,
  handleCloseStatusModal,
  handleCloseAddictionModal,
  handleUpdateClientStatus,
  handleUpdatePrimaryAddiction,
  isLoadingUpdateStatus,
  isLoadingUpdateAddiction,
  rehabs,
  selectedRehab,
  handleUpdateRehab,
}) {
  const mandatoryFormsComplete = [
    clientData.AggressionControlPolicyOk,
    clientData.ClientRightsOk,
    clientData.ConfidentialityPolicysOk,
    clientData.GeneralLiabilityOk,
    clientData.InformedConsentOk,
    clientData.PsychiatristServicesOk,
  ].every(Boolean);

  return (
    <section className="modern-client-profile-header">
      <div className="modern-client-identity-card">
        <div className="modern-client-identity-main">
          <IconButton className="modern-client-back" onClick={handleBackClick} aria-label="Back to clients">
            <ArrowBackIcon />
          </IconButton>

          <div className="modern-client-avatar-wrap">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <IconButton
                  className="modern-client-avatar-edit"
                  aria-label="Upload profile picture"
                  onClick={() => document.getElementById("contained-button-file").click()}
                >
                  <EditIcon />
                </IconButton>
              }
            >
              <button className="modern-client-avatar-button" onClick={handleOpenModalPhoto} type="button">
                <Avatar src={clientData.profilePicture} className="modern-client-avatar" />
              </button>
            </Badge>
            <input accept="image/*" id="contained-button-file" multiple type="file" onChange={handleProfilePictureChange} />
          </div>

          <div className="modern-client-identity-copy">
            <div className="modern-client-name-row">
              <div className="modern-client-name">
                {clientData.firstName} {clientData.lastName}
              </div>
              <div
                className={`modern-client-rehab-control ${
                  selectedRehab?.country === "Philippines" ? "is-ph" : "is-th"
                }`}
              >
                <span className="modern-client-rehab-code">
                  {selectedRehab?.country === "Philippines" ? "PH" : "TH"}
                </span>
                <label htmlFor="client-profile-rehab">Rehab</label>
                <select
                  id="client-profile-rehab"
                  value={selectedRehab?.id || ""}
                  onChange={handleUpdateRehab}
                >
                  {rehabs.map((rehab) => (
                    <option key={rehab.id} value={rehab.id}>
                      {rehab.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modern-client-demographics">
              {[clientData.age !== "NA" && clientData.age ? `${clientData.age} years old` : null, clientData.gender !== "NA" && clientData.gender ? clientData.gender : null].filter(Boolean).join(" · ") || "Client profile"}
            </div>
            <div className="modern-client-meta">
              <span>{clientData.selectedTherapist ? `Assigned therapist: ${clientData.selectedTherapist}` : "No therapist assigned yet"}</span>
              <span>{clientData.clientIdentifier ? `Client ID: ${clientData.clientIdentifier}` : "No client identifier"}</span>
            </div>
          </div>

          <div className="modern-client-search"><SearchBar /></div>
        </div>

        <div className="modern-client-facts" aria-label="Client key information">
          <div className="modern-client-fact modern-client-status-fact modern-client-inline-fact">
            <span className="modern-client-fact-label">Status</span>
            <StatusRibon
              status={clientData.clientStatus}
              rehabId={clientData.rehabId}
              rehabCountry={clientData.rehabCountry}
              onClick={handleOpenStatusModal}
              isAllPreadmissionFieldsFilled={isAllPreadmissionFieldsFilled}
            />
            {openStatusModal && (
              <div className="profile-inline-editor modern-client-inline-editor">
                <span className="profile-inline-editor-kicker">Status</span>
                <DropDownComponentSorted
                  api="clientStatus"
                  value={newClientStatus}
                  onChange={handleClientStatusChange}
                  title="Client status"
                  displayInHouseAsActive={
                    clientData.rehabId === "miracles_asia_philippines" ||
                    clientData.rehabCountry === "Philippines"
                  }
                />
                <div className="profile-inline-editor-actions">
                  <Button size="small" onClick={handleCloseStatusModal}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleUpdateClientStatus} disabled={isLoadingUpdateStatus}>
                    {isLoadingUpdateStatus ? "Updating..." : "Save"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="modern-client-fact modern-client-inline-fact">
            <span className="modern-client-fact-label">Primary addiction</span>
            <MainAddictionRibon mainAddiction={clientData.primaryAddiction} addictionOther={clientData.addictionOther} onClick={handleOpenAddictionModal} />
            {openPrimaryAddictionModal && (
              <div className="profile-inline-editor modern-client-inline-editor">
                <span className="profile-inline-editor-kicker">Primary addiction</span>
                <DropDownComponent api="addictions" value={newClientAddiction} onChange={handleClientAddictionChange} title="Primary addiction" />
                <div className="profile-inline-editor-actions">
                  <Button size="small" onClick={handleCloseAddictionModal}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleUpdatePrimaryAddiction} disabled={isLoadingUpdateAddiction}>
                    {isLoadingUpdateAddiction ? "Updating..." : "Save"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="modern-client-fact"><span className="modern-client-fact-label">Admission date</span><div className="modern-client-fact-value">{clientData.dateOfAdmission ? formatDate(clientData.dateOfAdmission) : "Not set"}</div></div>
          <div className="modern-client-fact"><span className="modern-client-fact-label">Duration of stay</span><div className="modern-client-fact-value">{clientData.durationOfStay || "Not set"}</div></div>
          <div className="modern-client-fact"><span className="modern-client-fact-label">Mandatory forms</span><div className={`modern-client-fact-value modern-client-check-value ${mandatoryFormsComplete ? "is-complete" : ""}`}><CheckRibon check={mandatoryFormsComplete} labelContent={mandatoryFormsComplete ? "Complete" : "Incomplete"} /></div></div>
        </div>
      </div>
    </section>
  );
}

export default ClientProfileHeaderModern;
